import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { AppLayout } from './components/layout/AppLayout'
import { useAuthStore } from './store/authStore'
import { useUIStore } from './store/uiStore'
import { SkeletonDashboard } from './components/ui/Skeleton'
import { ErrorBoundary } from './components/shared/ErrorBoundary'

const Landing = lazy(() => import('./pages/Landing'))
const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Products = lazy(() => import('./pages/Products'))
const Orders = lazy(() => import('./pages/Orders'))
const Customers = lazy(() => import('./pages/Customers'))
const SetupWizard = lazy(() => import('./pages/SetupWizard'))
const ContentGenerator = lazy(() => import('./pages/ContentGenerator'))
const CompetitorResearch = lazy(() => import('./pages/CompetitorResearch'))
const Settings = lazy(() => import('./pages/Settings'))
const Admin = lazy(() => import('./pages/Admin'))
const VAGuide = lazy(() => import('./pages/VAGuide'))
const ContentLibrary = lazy(() => import('./pages/ContentLibrary'))
const FaqGenerator = lazy(() => import('./pages/FaqGenerator'))
const ChargebackBuilder = lazy(() => import('./pages/ChargebackBuilder'))
const SocialGenerator = lazy(() => import('./pages/SocialGenerator'))
const ProductOptimizer = lazy(() => import('./pages/ProductOptimizer'))
const AdCopyRemixer = lazy(() => import('./pages/AdCopyRemixer'))
const ProfitCalculator = lazy(() => import('./pages/ProfitCalculator'))
const BrandingGenerator = lazy(() => import('./pages/BrandingGenerator'))
const InfluencerOutreach = lazy(() => import('./pages/InfluencerOutreach'))
const ReviewGenerator = lazy(() => import('./pages/ReviewGenerator'))
const StoreChecklist = lazy(() => import('./pages/StoreChecklist'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
})

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, token } = useAuthStore()
  if (!isAuthenticated && !token) {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (!user?.isAdmin) {
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

function PageLoader() {
  return (
    <div className="p-6">
      <SkeletonDashboard />
    </div>
  )
}

export default function App() {
  const { fetchMe, isAuthenticated, token } = useAuthStore()
  const { theme } = useUIStore()

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
  }, [theme])

  useEffect(() => {
    if (token && !isAuthenticated) {
      void fetchMe()
    }
  }, [token, isAuthenticated, fetchMe])

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Protected App */}
            <Route
              element={
                <RequireAuth>
                  <AppLayout />
                </RequireAuth>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/products" element={<Products />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/wizard" element={<SetupWizard />} />
              <Route path="/content" element={<ContentGenerator />} />
              <Route path="/content-library" element={<ContentLibrary />} />
              <Route path="/research" element={<CompetitorResearch />} />
              <Route path="/va-guide" element={<VAGuide />} />
              <Route path="/faq-generator" element={<FaqGenerator />} />
              <Route path="/chargeback" element={<ChargebackBuilder />} />
              <Route path="/social-generator" element={<SocialGenerator />} />
              <Route path="/product-optimizer" element={<ProductOptimizer />} />
              <Route path="/ad-remixer" element={<AdCopyRemixer />} />
              <Route path="/profit-calculator" element={<ProfitCalculator />} />
              <Route path="/branding" element={<BrandingGenerator />} />
              <Route path="/influencer-outreach" element={<InfluencerOutreach />} />
              <Route path="/review-generator" element={<ReviewGenerator />} />
              <Route path="/store-checklist" element={<StoreChecklist />} />
              <Route path="/settings" element={<Settings />} />
              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <Admin />
                  </RequireAdmin>
                }
              />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </BrowserRouter>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3500,
          style: {
            background: '#1e293b',
            color: '#f8fafc',
            border: '1px solid #334155',
            borderRadius: '12px',
            fontSize: '14px',
          },
          success: { iconTheme: { primary: '#10b981', secondary: '#f8fafc' } },
          error: { iconTheme: { primary: '#ef4444', secondary: '#f8fafc' } },
        }}
      />
    </QueryClientProvider>
  )
}
